import * as React from 'react'

import { BuilderDroppableElement, ConditionValue } from '@makyoapp/makyo-common'
import { FormContainer } from '@makyoapp/makyo-tw-ui'

import { IAppLayer } from '@/core/app-builder/app/layer'
import { CompContextProvider, useCompBackgroundColor, useCompContext } from '@/context/__project__/CompContext'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { ComponentBehaviorKey, FormComponentProps } from '@/shared/builder'
import CompAccordionWrapper from '@/components/__project__/CompAccordion'
import { CompAlertWrapper } from '@/components/__project__/CompAlertWrapper'
import CompArrayGalleryWrapper from '@/components/__project__/CompArrayGallery'
import CompAudioWrapper from '@/components/__project__/CompAudio'
import CompButtonWrapper from '@/components/__project__/CompButton'
import CompCalendarWrapper from '@/components/__project__/CompCalendar'
import CompCardWrapper from '@/components/__project__/CompCard'
import CompChartBarWrapper from '@/components/__project__/CompChartBar'
import CompChartPieWrapper from '@/components/__project__/CompChartPie'
import CompCheckboxWrapper from '@/components/__project__/CompCheckbox'
import CompCircularProgressWrapper from '@/components/__project__/CompCircularProgress'
import CompCommentWrapper from '@/components/__project__/CompComment'
import CompDataTableWrapper from '@/components/__project__/CompDataTable'
import CompDateTimePickerWrapper from '@/components/__project__/CompDateTimePicker'
import CompDividerWrapper from '@/components/__project__/CompDivider'
import CompFabWrapper from '@/components/__project__/CompFab'
import CompFieldRatingWrapper from '@/components/__project__/CompFieldRating'
import CompFileUploadWrapper from '@/components/__project__/CompFileUpload'
import CompFormWrapper from '@/components/__project__/CompForm'
import CompGalleryWrapper from '@/components/__project__/CompGallery'
import CompHiddenFieldWrapper from '@/components/__project__/CompHiddenField'
import CompIconButtonWrapper from '@/components/__project__/CompIconButton'
import CompIconPickerWrapper from '@/components/__project__/CompIconPicker'
import CompImageWrapper from '@/components/__project__/CompImage'
import CompImageUploadWrapper from '@/components/__project__/CompImageUpload'
import CompLinearProgressWrapper from '@/components/__project__/CompLinearProgress'
import CompListWrapper from '@/components/__project__/CompList'
import CompLocationWrapper from '@/components/__project__/CompLocation'
import CompLocationMultiWrapper from '@/components/__project__/CompLocationMulti'
import CompLoginFormWrapper from '@/components/__project__/CompLoginForm'
import { CompNewsletterWrapper } from '@/components/__project__/CompNewsletter'
import CompPhoneInputWrapper from '@/components/__project__/CompPhoneInput/CompPhoneInputWrapper'
import CompPricingWrapper from '@/components/__project__/CompPricing'
import CompPromptWrapper from '@/components/__project__/CompPromptWrapper'
import CompQuantityWrapper from '@/components/__project__/CompQuantityWrapper'
import CompRatingWrapper from '@/components/__project__/CompRating'
import CompRichTextWrapper from '@/components/__project__/CompRichText'
import CompSectionWrapper from '@/components/__project__/CompSection'
import CompSelectWrapper from '@/components/__project__/CompSelect'
import CompShoppingCartWrapper from '@/components/__project__/CompShoppingCart'
import CompSingleRowWrapper from '@/components/__project__/CompSingleRow'
import CompSliderWrapper from '@/components/__project__/CompSlider'
import CompSwitchWrapper from '@/components/__project__/CompSwitch'
import CompTabsWrapper from '@/components/__project__/CompTabs'
import CompTextWrapper from '@/components/__project__/CompText'
import CompTextFieldWrapper from '@/components/__project__/CompTextField'
import CompTimelineWrapper from '@/components/__project__/CompTimeline'
import CompVerticalMenuWrapper from '@/components/__project__/CompVerticalMenu'
import CompVideoWrapper from '@/components/__project__/CompVideo'

const CONTAINER_MAP = {
  ['form']: CompFormWrapper,
  ['section']: CompSectionWrapper,
  ['tabs']: CompTabsWrapper,
  ['header']: CompSectionWrapper,
  ['footer']: CompSectionWrapper,
} as const

// Component mapping for non-container components
const COMPONENT_MAP = {
  ['text-field']: CompTextFieldWrapper,
  ['datetime-picker']: CompDateTimePickerWrapper,
  ['file']: CompFileUploadWrapper,
  ['image-upload']: CompImageUploadWrapper,
  ['selection']: CompSelectWrapper,
  ['checkbox']: CompCheckboxWrapper,
  ['switch']: CompSwitchWrapper,
  ['slider']: CompSliderWrapper,
  ['hidden-field']: CompHiddenFieldWrapper,
  ['button']: CompButtonWrapper,
  ['icon']: CompIconButtonWrapper,
  ['video']: CompVideoWrapper,
  ['audio']: CompAudioWrapper,
  ['image']: CompImageWrapper,
  ['text']: CompTextWrapper,
  ['rich-text']: CompRichTextWrapper,
  ['fab']: CompFabWrapper,
  ['data-list']: CompSingleRowWrapper,
  ['list']: CompListWrapper,
  ['card']: CompCardWrapper,
  ['array-gallery']: CompArrayGalleryWrapper,
  ['gallery']: CompGalleryWrapper,
  ['multi-location']: CompLocationMultiWrapper,
  ['map']: CompLocationWrapper,
  ['login-form']: CompLoginFormWrapper,
  ['data-table']: CompDataTableWrapper,
  ['divider']: CompDividerWrapper,
  ['accordion']: CompAccordionWrapper,
  ['linear-progress']: CompLinearProgressWrapper,
  ['circular-progress']: CompCircularProgressWrapper,
  ['pie']: CompChartPieWrapper,
  ['bar']: CompChartBarWrapper,
  ['field-rating']: CompFieldRatingWrapper,
  ['rating']: CompRatingWrapper,
  ['vertical-menu']: CompVerticalMenuWrapper,
  ['shopping-cart']: CompShoppingCartWrapper,
  ['comment']: CompCommentWrapper,
  ['timeline']: CompTimelineWrapper,
  ['calendar']: CompCalendarWrapper,
  ['prompt']: CompPromptWrapper,
  ['quantity']: CompQuantityWrapper,
  ['pricing']: CompPricingWrapper,
  ['newsletter']: CompNewsletterWrapper,
  ['alert']: CompAlertWrapper,
  ['icon-picker']: CompIconPickerWrapper,
  ['phone-input']: CompPhoneInputWrapper,
} as const

export interface BuilderComponentCoreProps {
  containerClient?: IAppLayer
  rowId?: string
  detailRowIndex?: number
  element: BuilderDroppableElement
  ancestors?: BuilderDroppableElement[]
  formProps?: FormComponentProps
  className?: string
  index: number
}

/**
 * Core building block that renders each page element/body as component
 */
export const BuilderComponentCore = React.memo(
  React.forwardRef<HTMLDivElement, BuilderComponentCoreProps>((props, ref) => {
    // pre-access check visibility
    const { checkVisibility } = useCheckVisibilityV2()

    // Destructure props
    const { ancestors = [], containerClient, rowId, element, formProps, detailRowIndex } = props

    // Get component from container client
    const component = containerClient?.componentsMap.get(element.id)
    const detailTableId = containerClient?.settings._TableID

    if (!containerClient || !component) return null

    // handle component visibility
    const visibility = component.settings[ComponentBehaviorKey.Visibility] as ConditionValue | undefined

    const isVisible = visibility
      ? checkVisibility({
          visibility,
          formState: formProps?.formState,
          layerTableId: detailTableId,
          layerRowIndex: detailRowIndex,
        })
      : true

    if (!isVisible) return null

    return (
      <CompContextProvider
        key={element.id}
        ancestors={ancestors}
        containerClient={containerClient}
        component={component}
        rowId={rowId}
        detailRowIndex={detailRowIndex}
        formProps={formProps}
      >
        <React.Suspense fallback={null}>
          <RenderComponent element={element} />
        </React.Suspense>
      </CompContextProvider>
    )
  })
)
BuilderComponentCore.displayName = 'BuilderComponentCore'

/**
 * Render component
 */
const RenderComponent = ({ element }: { element: BuilderDroppableElement }) => {
  const compContext = useCompContext()
  const { currentParentBg } = useCompBackgroundColor()

  // access comp context
  const { ancestors, component, settings, containerClient, detailRowIndex, rowId, formProps } = compContext

  // Allow containers to render without settings, except 'tabs' which requires settings
  const isContainer = ['form', 'section', 'header', 'footer', 'tab-item'].includes(element.compId)
  const noSettings = !settings || Object.keys(settings).length === 0

  if (!component) {
    return null
  }
  if (!isContainer && noSettings) {
    return null
  }

  const renderComponent = () => {
    // Handle parent elements (container) that require children rendering
    switch (element.compId) {
      case 'form': {
        const FormWrapper = CONTAINER_MAP['form']
        return (
          <FormWrapper>
            {(props: FormComponentProps) => {
              return element?.children?.map((childEl, index) => {
                return childEl ? (
                  <BuilderComponentCore
                    key={childEl?.id}
                    index={index}
                    containerClient={containerClient}
                    rowId={rowId}
                    detailRowIndex={detailRowIndex}
                    element={childEl}
                    ancestors={[...ancestors, element]}
                    formProps={props}
                  />
                ) : null
              })
            }}
          </FormWrapper>
        )
      }
      case 'section':
      case 'header':
      case 'footer': {
        const SectionWrapper = CONTAINER_MAP['section']
        return (
          <SectionWrapper>
            {() => {
              return element?.children?.map((childEl, index) => {
                return childEl ? (
                  <BuilderComponentCore
                    key={childEl?.id}
                    index={index}
                    containerClient={containerClient}
                    rowId={rowId}
                    detailRowIndex={detailRowIndex}
                    element={childEl}
                    ancestors={[...ancestors, element]}
                    formProps={formProps}
                  />
                ) : null
              })
            }}
          </SectionWrapper>
        )
      }
      case 'tabs': {
        const TabsWrapper = CONTAINER_MAP['tabs']
        return (
          <TabsWrapper element={element}>
            {({ tabElement }: { tabElement: BuilderDroppableElement }) => {
              return tabElement.children.map((tabItemEl, index) => {
                if (!tabItemEl) return null
                return (
                  <BuilderComponentCore
                    key={tabItemEl.id}
                    index={index}
                    containerClient={containerClient}
                    rowId={rowId}
                    detailRowIndex={detailRowIndex}
                    element={tabItemEl}
                    ancestors={[...ancestors, element, tabElement]}
                    formProps={formProps}
                  />
                )
              })
            }}
          </TabsWrapper>
        )
      }
      default: {
        break
      }
    }

    // Handle simple components using the component map
    const Component = COMPONENT_MAP[element.compId as keyof typeof COMPONENT_MAP]
    if (Component) {
      return <Component />
    }

    // Default fallback for unknown components
    return <div className='min-h-[80px] flex items-center justify-center'>{element.title}</div>
  }

  // handle specific case where the (form) element does not have a form container parent
  if (component._GroupID === 'form') {
    // check if the element has form container
    const hasNoFormContainer = !ancestors?.find((a) => a.compId === 'form')

    if (hasNoFormContainer) {
      return (
        <FormContainer backgroundColor={currentParentBg} className='p-4'>
          {renderComponent()}
        </FormContainer>
      )
    }
  }

  return renderComponent()
}
